import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, BookOpen, Menu, X, Type, Bookmark, BookmarkCheck } from 'lucide-react';
import type { Book, Chapter, Volume } from '../../types';
import { numberToChinese } from '../../utils/numberToChinese';
import {
  applyReaderSettings, loadLocalReaderSettings, saveLocalReaderSettings, scrollProgress,
  normalizeReaderSettings, DEFAULT_READER_SETTINGS, READER_SETTINGS_PREF,
  type ReaderSettings,
} from './readerSettings';
import { ReaderSettingsPanel } from './ReaderSettingsPanel';
import './styles/reader.css';

function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  doc.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((el) => el.remove());
  doc.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
    });
  });
  return doc.body.innerHTML;
}

function lastReadKey(bookId: string): string {
  return `reader_last_${bookId}`;
}

function bookmarksKey(bookId: string): string {
  return `reader_bookmarks_${bookId}`;
}

interface BookmarkEntry {
  chapterId: string;
  chapterTitle: string;
  /** 章内滚动位置（比例 0-1），恢复时用 */
  ratio: number;
  createdAt: number;
}

function loadBookmarks(bookId: string): BookmarkEntry[] {
  try {
    const raw = localStorage.getItem(bookmarksKey(bookId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((b): b is BookmarkEntry =>
      !!b && typeof (b as BookmarkEntry).chapterId === 'string');
  } catch {
    return [];
  }
}

export function Reader() {
  const { id } = useParams();
  const bookId = id || '';
  const navigate = useNavigate();

  const [book, setBook] = useState<Book | null>(null);
  const [volumes, setVolumes] = useState<Volume[]>([]);
  const [chaptersMap, setChaptersMap] = useState<Record<string, Chapter[]>>({});
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bookmarks, setBookmarks] = useState<BookmarkEntry[]>([]);
  // 首屏用本地缓存，避免加载偏好期间闪烁；随后再从服务端同步
  const [settings, setSettings] = useState<ReaderSettings>(() => loadLocalReaderSettings());
  const mainRef = useRef<HTMLElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // 排版设置即时生效（CSS 变量 + 主题 data 属性）
  useEffect(() => {
    applyReaderSettings(rootRef.current, settings);
  }, [settings]);

  const updateSettings = useCallback((patch: Partial<ReaderSettings>) => {
    setSettings((prev) => {
      const next = normalizeReaderSettings({ ...prev, ...patch });
      saveLocalReaderSettings(next);
      // 持久化到服务端偏好：换设备后排版一致
      void window.api.setUserPreference(READER_SETTINGS_PREF, JSON.stringify(next)).catch(() => { /* 本地已存 */ });
      return next;
    });
  }, []);

  // 启动时从服务端偏好恢复排版设置
  useEffect(() => {
    let cancelled = false;
    window.api.getUserPreference(READER_SETTINGS_PREF)
      .then((pref) => {
        const raw = pref?.preference_value;
        if (cancelled || !raw) return;
        try {
          setSettings(normalizeReaderSettings(JSON.parse(raw)));
        } catch { /* 偏好损坏时保留本地值 */ }
      })
      .catch(() => { /* 无偏好时用本地/默认值 */ });
    return () => { cancelled = true; };
  }, []);

  // 书签随作品加载
  useEffect(() => {
    if (bookId) setBookmarks(loadBookmarks(bookId));
  }, [bookId]);

  const persistBookmarks = useCallback((next: BookmarkEntry[]) => {
    setBookmarks(next);
    try {
      localStorage.setItem(bookmarksKey(bookId), JSON.stringify(next));
    } catch { /* 存储不可用时仅内存保留 */ }
  }, [bookId]);

  // 滚动进度：用于顶部进度条与"读到哪了"
  const handleScroll = useCallback(() => {
    const el = mainRef.current;
    if (!el) return;
    setProgress(scrollProgress(el.scrollTop, el.scrollHeight, el.clientHeight));
  }, []);

  // 切换章节后滚动回顶部（并同步进度）
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
    setProgress(0);
  }, [selectedChapter?.id]);

  const flatChapters = useMemo(() => {
    const all: Array<{ chapter: Chapter; volume: Volume }> = [];
    for (const vol of volumes) {
      for (const ch of chaptersMap[vol.id] || []) all.push({ chapter: ch, volume: vol });
    }
    all.sort((a, b) => a.chapter.chapter_number - b.chapter.chapter_number);
    return all;
  }, [volumes, chaptersMap]);

  const selectChapter = useCallback(async (chapter: Chapter) => {
    setSelectedChapter(chapter);
    setSidebarOpen(false);
    localStorage.setItem(lastReadKey(bookId), chapter.id);
    // 与编辑器共用「上次位置」记忆：进入作品后默认打开上次编辑或阅读的章节
    void window.api.setUserPreference(`lastChapter_${bookId}`, String(chapter.id)).catch(() => { /* ignore */ });
    const c = await window.api.getChapterContent(chapter.id).catch(() => '');
    setContent(sanitizeHtml(c));
  }, [bookId]);

  const loadBook = useCallback(async () => {
    if (!bookId) return;
    setLoading(true);
    try {
      const [books, vols] = await Promise.all([window.api.getBooks(), window.api.getVolumes(bookId)]);
      const b = books.find((x) => x.id === bookId) ?? null;
      setBook(b);
      const map: Record<string, Chapter[]> = {};
      const chapList = await Promise.all(vols.map(async (v) => {
        const chs = await window.api.getChapters(v.id);
        map[v.id] = chs;
        return chs;
      }));
      setVolumes(vols);
      setChaptersMap(map);

      const all = chapList.flat().sort((a, b) => a.chapter_number - b.chapter_number);
      const saved = localStorage.getItem(lastReadKey(bookId));
      const target = all.find((c) => c.id === saved) ?? all[0] ?? null;
      if (target) await selectChapter(target);
    } finally {
      setLoading(false);
    }
  }, [bookId, selectChapter]);

  useEffect(() => {
    void loadBook();
  }, [loadBook]);

  const currentIndex = flatChapters.findIndex((x) => x.chapter.id === selectedChapter?.id);
  const goto = useCallback((index: number) => {
    const item = flatChapters[index];
    if (item) void selectChapter(item.chapter);
  }, [flatChapters, selectChapter]);

  // 键盘翻页/滚动：左右（或 j/k）翻章，空格/PageDown 下滚一屏
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // 输入框内不劫持按键（设置面板里的输入）
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const el = mainRef.current;
      switch (e.key) {
        case 'ArrowLeft':
        case 'k':
          e.preventDefault();
          goto(currentIndex - 1);
          break;
        case 'ArrowRight':
        case 'j':
          e.preventDefault();
          goto(currentIndex + 1);
          break;
        case ' ':
        case 'PageDown':
          e.preventDefault();
          el?.scrollBy({ top: (el.clientHeight || 400) * 0.9, behavior: 'smooth' });
          break;
        case 'PageUp':
          e.preventDefault();
          el?.scrollBy({ top: -(el.clientHeight || 400) * 0.9, behavior: 'smooth' });
          break;
        case 'Home':
          e.preventDefault();
          el?.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        case 'End':
          e.preventDefault();
          el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          break;
        case 'Escape':
          if (sidebarOpen) setSidebarOpen(false);
          else if (showSettings) setShowSettings(false);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentIndex, goto, sidebarOpen, showSettings]);

  /** 当前位置是否已加书签 */
  const currentBookmark = useMemo(
    () => bookmarks.find((b) => b.chapterId === selectedChapter?.id) ?? null,
    [bookmarks, selectedChapter?.id],
  );

  const toggleBookmark = useCallback(() => {
    const el = mainRef.current;
    const chapter = selectedChapter;
    if (!chapter) return;
    if (currentBookmark) {
      persistBookmarks(bookmarks.filter((b) => b.chapterId !== chapter.id));
      return;
    }
    const ratio = el && el.scrollHeight > el.clientHeight
      ? el.scrollTop / (el.scrollHeight - el.clientHeight)
      : 0;
    persistBookmarks([
      ...bookmarks,
      { chapterId: chapter.id, chapterTitle: chapter.title, ratio, createdAt: Date.now() },
    ]);
  }, [bookmarks, currentBookmark, persistBookmarks, selectedChapter]);

  /** 跳转到书签（含章内位置） */
  const openBookmark = useCallback(async (entry: BookmarkEntry) => {
    const item = flatChapters.find((x) => x.chapter.id === entry.chapterId);
    if (!item) return;
    await selectChapter(item.chapter);
    // 等渲染完成后再恢复章内位置
    requestAnimationFrame(() => {
      const el = mainRef.current;
      if (!el) return;
      const scrollable = el.scrollHeight - el.clientHeight;
      el.scrollTop = scrollable > 0 ? Math.round(scrollable * entry.ratio) : 0;
    });
  }, [flatChapters, selectChapter]);

  // 当前章节所属卷（用于标题下方副标题）
  const currentVolume = useMemo(() => {
    if (!selectedChapter) return null;
    return flatChapters.find(x => x.chapter.id === selectedChapter.id)?.volume ?? null;
  }, [flatChapters, selectedChapter]);
  const currentVolumeNumber = useMemo(() => {
    if (!currentVolume) return 0;
    return volumes.indexOf(currentVolume) + 1;
  }, [volumes, currentVolume]);

  return (
    <div className="reader-root" ref={rootRef}>
      <header className="reader-header">
        <button className="reader-icon-btn" onClick={() => navigate('/home')} title="返回书架">
          <ArrowLeft size={17} />
        </button>
        <div className="reader-header-title">
          <strong>{book?.title ?? '阅读'}</strong>
          {selectedChapter && (
            <span>第{numberToChinese(selectedChapter.chapter_number)}章 {selectedChapter.title}</span>
          )}
        </div>
        <button
          className="reader-icon-btn"
          onClick={toggleBookmark}
          disabled={!selectedChapter}
          title={currentBookmark ? '移除本章书签' : '为本章加书签'}
          aria-label={currentBookmark ? '移除本章书签' : '为本章加书签'}
        >
          {currentBookmark ? <BookmarkCheck size={17} /> : <Bookmark size={17} />}
        </button>
        <button
          className="reader-icon-btn"
          onClick={() => setShowSettings(v => !v)}
          title="阅读设置（字号/行距/主题）"
          aria-label="阅读设置"
          aria-expanded={showSettings}
        >
          <Type size={17} />
        </button>
        <button className="reader-icon-btn reader-menu-btn" onClick={() => setSidebarOpen(v => !v)} title="目录">
          {sidebarOpen ? <X size={17} /> : <Menu size={17} />}
        </button>

        {showSettings && (
          <ReaderSettingsPanel
            settings={settings}
            onChange={updateSettings}
            onReset={() => updateSettings(DEFAULT_READER_SETTINGS)}
            onClose={() => setShowSettings(false)}
          />
        )}
      </header>

      {/* 阅读进度：细线不占布局高度 */}
      <div className="reader-progress-track" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
        <div className="reader-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className={`reader-body${sidebarOpen ? ' sidebar-open' : ''}`}>
        <aside className="reader-sidebar">
          <div className="reader-sidebar-inner">
            {bookmarks.length > 0 && (
              <div className="reader-vol-group reader-bookmark-group">
                <div className="reader-vol-title">书签（{bookmarks.length}）</div>
                <ul className="reader-chapter-list">
                  {bookmarks.map((b) => (
                    <li key={b.chapterId}>
                      <button
                        className={`reader-chapter-btn${selectedChapter?.id === b.chapterId ? ' is-active' : ''}`}
                        onClick={() => void openBookmark(b)}
                        title="跳转到书签位置"
                      >
                        <BookmarkCheck size={12} /> {b.chapterTitle || '（未命名章节）'}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {volumes.length === 0 && !loading && (
              <div className="reader-empty">暂无章节，先去创作页写一章吧。</div>
            )}
            {volumes.map((vol) => {
              const chs = chaptersMap[vol.id] || [];
              return (
                <div key={vol.id} className="reader-vol-group">
                  <div className="reader-vol-title">第{numberToChinese(volumes.indexOf(vol) + 1)}卷 {vol.title}</div>
                  <ul className="reader-chapter-list">
                    {chs.map((ch) => (
                      <li key={ch.id}>
                        <button
                          className={`reader-chapter-btn${selectedChapter?.id === ch.id ? ' is-active' : ''}`}
                          onClick={() => void selectChapter(ch)}
                        >
                          第{numberToChinese(ch.chapter_number)}章 {ch.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </aside>

        <main className="reader-main" ref={mainRef} onScroll={handleScroll} tabIndex={-1}>
          {loading ? (
            <div className="reader-loading">正在加载...</div>
          ) : selectedChapter ? (
            <article className="reader-article">
              <h1 className="reader-chapter-heading">
                第{numberToChinese(selectedChapter.chapter_number)}章 {selectedChapter.title}
              </h1>
              {currentVolume && (
                <div className="reader-chapter-subtitle">
                  <span>第{numberToChinese(currentVolumeNumber)}卷 · {currentVolume.title}</span>
                  {typeof selectedChapter.word_count === 'number' && selectedChapter.word_count > 0 && (
                    <span className="reader-chapter-subtitle-dot">·</span>
                  )}
                  {typeof selectedChapter.word_count === 'number' && selectedChapter.word_count > 0 && (
                    <span>{selectedChapter.word_count.toLocaleString()} 字</span>
                  )}
                  <span className="reader-chapter-subtitle-dot">·</span>
                  <span>{progress}%</span>
                </div>
              )}
              {selectedChapter.summary && (
                <blockquote className="reader-summary">{selectedChapter.summary}</blockquote>
              )}
              <div className="reader-content" dangerouslySetInnerHTML={{ __html: content }} />
              <div className="reader-nav">
                <button
                  className="reader-nav-btn"
                  disabled={currentIndex <= 0}
                  onClick={() => goto(currentIndex - 1)}
                >
                  <ChevronLeft size={15} /> 上一章
                </button>
                <span className="reader-nav-hint">← → 翻章 · 空格下滚</span>
                <button
                  className="reader-nav-btn"
                  disabled={currentIndex < 0 || currentIndex >= flatChapters.length - 1}
                  onClick={() => goto(currentIndex + 1)}
                >
                  下一章 <ChevronRight size={15} />
                </button>
              </div>
            </article>
          ) : (
            <div className="reader-loading">
              <BookOpen size={36} />
              <p>选择章节开始阅读</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
